import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, BehaviorSubject } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { ConfigService } from './config.service';
import { ApiResponse } from '../shared/models';

export interface AvailableTenantDto {
  domainName: string;
  displayName: string;
  redirectUrl: string;
  sortOrder: number;
  isCurrent: boolean;
}

export interface SwitchTenantRequest {
  targetDomain: string;
}

export interface SwitchTenantResponse {
  exchangeToken: string;
  redirectUrl: string;
  domainName: string;
  userId: number;
  userName: string;
}

export interface ExchangeTenantTokenResponse {
  token: string;
  domainName: string;
  userId: number;
  userName: string;
}

@Injectable({
  providedIn: 'root'
})
export class TenantSwitchService {
  private apiUrl: string;
  private availableTenantsSubject = new BehaviorSubject<AvailableTenantDto[]>([]);
  public availableTenants$ = this.availableTenantsSubject.asObservable();
  
  private currentTenantSubject = new BehaviorSubject<AvailableTenantDto | null>(null);
  public currentTenant$ = this.currentTenantSubject.asObservable();

  constructor(private http: HttpClient, private configService: ConfigService) {
    this.apiUrl = `${this.configService.get('apiBaseUrl')}/TenantSwitch`;
  }

  /**
   * Load available tenants for current user
   */
  loadAvailableTenants(): Observable<AvailableTenantDto[]> {
    return this.http.get<any>(`${this.apiUrl}/available-tenants`).pipe(
      map(response => response.result || []),
      tap(tenants => {
        this.availableTenantsSubject.next(tenants);
        const current = tenants.find((t: AvailableTenantDto) => t.isCurrent);
        if (current) {
          this.currentTenantSubject.next(current);
        }
      })
    );
  }

  /**
   * Switch to different tenant
   * Returns redirect URL to navigate to
   */
  switchTenant(targetDomain: string): Observable<SwitchTenantResponse> {
    const request: SwitchTenantRequest = { targetDomain };
    return this.http.post<ApiResponse<SwitchTenantResponse>>(`${this.apiUrl}/switch`, request).pipe(
      map(response => {
        if (response?.success) {
          return response.result as SwitchTenantResponse;
        }
        throw new Error(response?.errorMessage?.[0]?.message || 'Failed to switch tenant');
      })
    );
  }

  exchangeToken(token: string): Observable<ExchangeTenantTokenResponse> {
    return this.http.post<ApiResponse<ExchangeTenantTokenResponse>>(`${this.apiUrl}/exchange-token`, { token }).pipe(
      map(response => {
        if (response?.success) {
          return response.result;
        }
        throw new Error(response?.errorMessage?.[0]?.message || 'Failed to exchange tenant switch token');
      })
    );
  }

  /**
   * Perform tenant switch with full redirect
   * Redirects to the target tenant with a one-time exchange token.
   */
  performTenantSwitch(targetDomain: string): void {
    this.switchTenant(targetDomain).subscribe({
      next: (response) => {
        window.location.href = response.redirectUrl;
      },
      error: (error) => {
        console.error('Failed to switch tenant:', error);
        throw error;
      }
    });
  }

  /**
   * Get current tenant
   */
  getCurrentTenant(): AvailableTenantDto | null {
    return this.currentTenantSubject.value;
  }

  /**
   * Check if user has multiple tenants (should show switcher)
   */
  hasMultipleTenants(): boolean {
    return this.availableTenantsSubject.value.length > 1;
  }

}
